import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTradeDraftQuote } from "@/hooks/useTradeDraftQuote";
import { TradeTargetCard } from "@/components/trade/TradeTargetCard";
import { TradeValueSummary } from "@/components/trade/TradeValueSummary";
import { toast } from "sonner";
import { InventoryIcon, InventoryName } from "@/components/InventoryIdentity";
import { inventoryVisual } from "@/lib/inventoryVisuals";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Search, Tag as TagIcon, Coins, X, Check, Loader2, HandCoins, Trophy, Hourglass, Package, Ticket, Wrench, Gem, ShieldCheck, Sword, Shield, Sparkles, FlaskConical, Pickaxe, Sprout, Flower, Fish, Axe, Shell, Shovel, Telescope, Diamond, Lock } from "lucide-react";

/**
 * Material key → lucide icon component. Keeps the request board / picker /
 * summary chips visually consistent with the profession badges over in
 * /adventure, where each material has its own bespoke icon rather than a
 * generic "gem" glyph. Falls back to Gem for anything unknown.
 */
const MATERIAL_ICON = {
  ore: Pickaxe,
  herb: Sprout,
  essence: Flower,
  pearl: Fish,
  gem: Diamond,
  wood: Axe,
  scale: Shell,
  relic_shard: Shovel,
  stardust: Telescope,
};

/** Item slot / kind → lucide icon. */
const GEAR_ICON = {
  weapon: Sword,
  armor: Shield,
  trinket: Sparkles,
  relic: Gem,
  consumable: FlaskConical,
};

const pickMaterialIcon = (key) => MATERIAL_ICON[key] || Gem;
const pickGearIcon = (item) => inventoryVisual(item).icon;

const RARITY_COLORS = {
  common: "#9ca3af", uncommon: "#34d399", rare: "#38bdf8",
  epic: "#a78bfa", legendary: "#fbbf24", goddess: "#f472b6",
};
const RARITY_ORDER = ["common", "uncommon", "rare", "epic", "legendary", "goddess"];
const GEAR_SLOTS = ["weapon", "armor", "trinket", "relic"];

const cardFree = (c) => !c.locked_in_request && !c.locked_in_trade && !c.locked_in_market && !c.locked_in_battle && !c.locked_in_show;

/* ─── Little building blocks ──────────────────────────────────────────── */

function CardThumb({ card, size = "sm", selected, onClick, testid }) {
  if (!card) return null;
  const color = RARITY_COLORS[card.rarity] || "#9ca3af";
  const w = size === "xs" ? "w-12" : size === "md" ? "w-20" : "w-16";
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`${w} shrink-0 rounded-lg overflow-hidden relative border-2 transition-transform ${onClick ? "hover:-translate-y-0.5 cursor-pointer" : "cursor-default"}`}
      style={{ borderColor: selected ? "#22d3ee" : color, boxShadow: selected ? "0 0 14px #22d3ee" : `0 0 8px ${color}40`, aspectRatio: "3/4" }}>
      {card.front_image
        ? <img src={card.front_image} alt={card.title} className="absolute inset-0 w-full h-full object-cover" />
        : <div className="absolute inset-0 bg-black/60" />}
      {selected && <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-cyan-400 grid place-items-center"><Check className="h-2.5 w-2.5 text-black" /></span>}
      <span className="absolute bottom-0 inset-x-0 bg-black/70 text-[8px] text-white/80 truncate px-1 py-0.5">{card.title}</span>
    </button>
  );
}

function TagInput({ tags, setTags }) {
  const [val, setVal] = useState("");
  const add = () => {
    const t = val.trim().toLowerCase();
    if (t && !tags.includes(t) && tags.length < 8) setTags([...tags, t]);
    setVal("");
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {tags.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded-full bg-cyan-400/10 border border-cyan-400/30 px-2 py-0.5 text-[11px] text-cyan-200">
            {t}
            <button type="button" onClick={() => setTags(tags.filter((x) => x !== t))}><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <Input data-testid="rb-tag-input" value={val} onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
        onBlur={add}
        placeholder="Tags for wanted cards (e.g. redhead, neon)…"
        className="bg-[hsl(220_18%_12%)] border-white/10" disabled={tags.length >= 8} />
    </div>
  );
}

/* Compact +/- stepper reused for tokens & materials. */
function Stepper({ value, max, onChange, testid }) {
  const q = Number(value) || 0;
  return (
    <div className="flex items-center gap-1.5 shrink-0">
      <button type="button" data-testid={`${testid}-dec`} onClick={() => onChange(Math.max(0, q - 1))}
        className="h-5 w-5 rounded bg-white/10 text-white/70 hover:bg-white/20 leading-none">−</button>
      <span className="font-mono text-xs w-6 text-center text-white">{q}</span>
      <button type="button" data-testid={`${testid}-inc`} disabled={max != null && q >= max}
        onClick={() => onChange(max != null ? Math.min(max, q + 1) : q + 1)}
        className="h-5 w-5 rounded bg-white/10 text-white/70 hover:bg-white/20 disabled:opacity-30 leading-none">+</button>
    </div>
  );
}

/* Grouped, expandable selector for the poster's/responder's inventory. */
function InventoryStakePicker({ inv, cards, selection, setSelection, includeCards = true, testidPrefix }) {
  const [tab, setTab] = useState("cards");
  // Per-tab search + filter state — makes finding one card in a fat collection
  // (>50 items) a one-second job instead of an infinite-scroll hunt.
  const [cardSearch, setCardSearch] = useState("");
  const [cardRarity, setCardRarity] = useState("all");
  const [cardPage, setCardPage] = useState(1);
  const [itemSearch, setItemSearch] = useState("");
  const [itemSlot, setItemSlot] = useState("all");
  const [itemTier, setItemTier] = useState("all");
  const [itemPage, setItemPage] = useState(1);
  const [packSearch, setPackSearch] = useState("");
  const [packPage, setPackPage] = useState(1);

  const PAGE_SIZE = 12;

  const freeCards = (cards || []).filter(cardFree);
  const freePacks = (inv?.packs || []).filter((p) => !p.locked_in_request && !p.locked_in_trade && !p.locked_in_market);
  const freeItems = (inv?.items || []).filter((it) => !it.equipped_on && !it.locked_in_request && !it.listed);
  const tokens = (inv?.tokens || []).filter((t) => t.qty > 0);
  const materials = inv?.materials || [];

  // ── Cards: search by title, filter by rarity, paginate ─────────
  const filteredCards = useMemo(() => {
    const q = cardSearch.trim().toLowerCase();
    return freeCards.filter((c) => {
      if (cardRarity !== "all" && c.rarity !== cardRarity) return false;
      if (q && !(c.title || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [freeCards, cardSearch, cardRarity]);
  const cardPages = Math.max(1, Math.ceil(filteredCards.length / PAGE_SIZE));
  const cardPageSafe = Math.min(cardPage, cardPages);
  const cardSlice = filteredCards.slice((cardPageSafe - 1) * PAGE_SIZE, cardPageSafe * PAGE_SIZE);
  useEffect(() => { setCardPage(1); }, [cardSearch, cardRarity]);

  // ── Gear: search by name, filter by slot + tier ────────────────
  const filteredItems = useMemo(() => {
    const q = itemSearch.trim().toLowerCase();
    return freeItems.filter((it) => {
      if (itemSlot !== "all" && (it.slot || it.kind) !== itemSlot) return false;
      if (itemTier !== "all" && String(it.tier) !== String(itemTier)) return false;
      if (q && !(it.name || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [freeItems, itemSearch, itemSlot, itemTier]);
  const itemPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const itemPageSafe = Math.min(itemPage, itemPages);
  const itemSlice = filteredItems.slice((itemPageSafe - 1) * PAGE_SIZE, itemPageSafe * PAGE_SIZE);
  useEffect(() => { setItemPage(1); }, [itemSearch, itemSlot, itemTier]);

  // ── Packs: search by name, paginate ────────────────────────────
  const filteredPacks = useMemo(() => {
    const q = packSearch.trim().toLowerCase();
    if (!q) return freePacks;
    return freePacks.filter((p) => (p.pack_name || "Card Pack").toLowerCase().includes(q));
  }, [freePacks, packSearch]);
  const packPages = Math.max(1, Math.ceil(filteredPacks.length / PAGE_SIZE));
  const packPageSafe = Math.min(packPage, packPages);
  const packSlice = filteredPacks.slice((packPageSafe - 1) * PAGE_SIZE, packPageSafe * PAGE_SIZE);
  useEffect(() => { setPackPage(1); }, [packSearch]);

  const toggle = (key, id) => {
    const list = selection[key] || [];
    setSelection({ ...selection, [key]: list.includes(id) ? list.filter((x) => x !== id) : [...list, id] });
  };

  // ── Selection summary chips (rendered at the bottom of the picker) ──
  const selCards = (selection.cardIds || []).map((id) => freeCards.find((c) => c.id === id)).filter(Boolean);
  const selPacks = (selection.packIds || []).map((id) => freePacks.find((p) => p.id === id)).filter(Boolean);
  const selItems = (selection.itemIds || []).map((id) => freeItems.find((it) => it.id === id)).filter(Boolean);
  const selTokenEntries = Object.entries(selection.tokens || {}).filter(([, q]) => q > 0);
  const selMatEntries = Object.entries(selection.materials || {}).filter(([, q]) => q > 0);
  const totalCount = selCards.length + selPacks.length + selItems.length +
                     selTokenEntries.reduce((n, [, q]) => n + q, 0) +
                     selMatEntries.reduce((n, [, q]) => n + q, 0);

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02]">
      <div className="flex items-center gap-1 p-1.5 border-b border-white/5 flex-wrap">
        {includeCards && <TabBtn active={tab === "cards"} onClick={() => setTab("cards")} testid={`${testidPrefix}-tab-cards`}>Cards {selection.cardIds?.length ? `· ${selection.cardIds.length}` : ""}</TabBtn>}
        <TabBtn active={tab === "packs"} onClick={() => setTab("packs")} testid={`${testidPrefix}-tab-packs`}>Packs {selection.packIds?.length ? `· ${selection.packIds.length}` : ""}</TabBtn>
        <TabBtn active={tab === "items"} onClick={() => setTab("items")} testid={`${testidPrefix}-tab-items`}>Gear {selection.itemIds?.length ? `· ${selection.itemIds.length}` : ""}</TabBtn>
        <TabBtn active={tab === "tokens"} onClick={() => setTab("tokens")} testid={`${testidPrefix}-tab-tokens`}>Tokens {Object.values(selection.tokens || {}).reduce((a, b) => a + b, 0) || ""}</TabBtn>
        <TabBtn active={tab === "materials"} onClick={() => setTab("materials")} testid={`${testidPrefix}-tab-materials`}>Mats {Object.values(selection.materials || {}).reduce((a, b) => a + b, 0) || ""}</TabBtn>
      </div>
      <div className="p-2">
        {includeCards && tab === "cards" && (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <div className="relative flex-1 min-w-[140px]">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
                <Input
                  data-testid={`${testidPrefix}-cards-search`}
                  value={cardSearch}
                  onChange={(e) => setCardSearch(e.target.value)}
                  placeholder="Search cards…"
                  className="pl-7 h-8 bg-[hsl(220_18%_12%)] border-white/10 text-xs"
                />
              </div>
              <select
                data-testid={`${testidPrefix}-cards-rarity`}
                value={cardRarity}
                onChange={(e) => setCardRarity(e.target.value)}
                className="h-8 rounded-md border border-white/10 bg-[hsl(220_18%_12%)] text-white text-xs px-2"
              >
                <option value="all">Any rarity</option>
                {RARITY_ORDER.map((r) => (<option key={r} value={r}>{r}</option>))}
              </select>
            </div>
            <div className="max-h-60 overflow-y-auto">
              <div className="flex flex-wrap gap-2" data-testid={`${testidPrefix}-cards`}>
                {cardSlice.length === 0 ? (
                  <span className="text-white/40 text-xs">{filteredCards.length === 0 ? "No free cards match." : "—"}</span>
                ) : cardSlice.map((c) => (
                  <CardThumb key={c.id} card={c} selected={(selection.cardIds || []).includes(c.id)}
                    testid={`${testidPrefix}-card-${c.id}`} onClick={() => toggle("cardIds", c.id)} />
                ))}
              </div>
            </div>
            <PickerPager page={cardPageSafe} pages={cardPages} onPage={setCardPage} total={filteredCards.length} testid={`${testidPrefix}-cards-pager`} />
          </>
        )}
        {tab === "packs" && (
          <>
            <div className="mb-2 relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
              <Input
                data-testid={`${testidPrefix}-packs-search`}
                value={packSearch}
                onChange={(e) => setPackSearch(e.target.value)}
                placeholder="Search packs…"
                className="pl-7 h-8 bg-[hsl(220_18%_12%)] border-white/10 text-xs"
              />
            </div>
            <div className="max-h-60 overflow-y-auto space-y-1" data-testid={`${testidPrefix}-packs`}>
              {packSlice.length === 0 ? <span className="text-white/40 text-xs">{freePacks.length === 0 ? "No unopened packs." : "No matches."}</span> : packSlice.map((p) => (
                <label key={p.id} data-testid={`${testidPrefix}-pack-${p.id}`}
                  className="flex items-center gap-2 rounded border border-white/10 bg-[hsl(220_18%_10%)] px-2 py-1.5 cursor-pointer hover:bg-white/5">
                  <input type="checkbox" checked={(selection.packIds || []).includes(p.id)} onChange={() => toggle("packIds", p.id)} className="accent-fuchsia-400" />
                  <Package className="h-3.5 w-3.5 text-fuchsia-300" />
                  <span className="text-[11px] text-white/70 truncate">{p.pack_name || "Card Pack"}</span>
                </label>
              ))}
            </div>
            <PickerPager page={packPageSafe} pages={packPages} onPage={setPackPage} total={filteredPacks.length} testid={`${testidPrefix}-packs-pager`} />
          </>
        )}
        {tab === "items" && (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <div className="relative flex-1 min-w-[140px]">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
                <Input
                  data-testid={`${testidPrefix}-items-search`}
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  placeholder="Search gear…"
                  className="pl-7 h-8 bg-[hsl(220_18%_12%)] border-white/10 text-xs"
                />
              </div>
              <select
                data-testid={`${testidPrefix}-items-slot`}
                value={itemSlot}
                onChange={(e) => setItemSlot(e.target.value)}
                className="h-8 rounded-md border border-white/10 bg-[hsl(220_18%_12%)] text-white text-xs px-2"
              >
                <option value="all">Any slot</option>
                {GEAR_SLOTS.map((s) => (<option key={s} value={s}>{s}</option>))}
              </select>
              <select
                data-testid={`${testidPrefix}-items-tier`}
                value={itemTier}
                onChange={(e) => setItemTier(e.target.value)}
                className="h-8 rounded-md border border-white/10 bg-[hsl(220_18%_12%)] text-white text-xs px-2"
              >
                <option value="all">Any tier</option>
                {[1, 2, 3, 4, 5].map((t) => (<option key={t} value={t}>T{t}</option>))}
              </select>
            </div>
            <div className="max-h-60 overflow-y-auto space-y-1" data-testid={`${testidPrefix}-items`}>
              {itemSlice.length === 0 ? <span className="text-white/40 text-xs">{freeItems.length === 0 ? "No spare gear." : "No matches."}</span> : itemSlice.map((it) => {
                const GI = pickGearIcon(it);
                return (
                  <label key={it.id} data-testid={`${testidPrefix}-item-${it.id}`} title={it.desc}
                    className="flex items-center gap-2 rounded border border-white/10 bg-[hsl(220_18%_10%)] px-2 py-1.5 cursor-pointer hover:bg-white/5">
                    <input type="checkbox" checked={(selection.itemIds || []).includes(it.id)} onChange={() => toggle("itemIds", it.id)} className="accent-amber-400" />
                    <InventoryIcon item={it} className="h-3.5 w-3.5" />
                    <InventoryName item={it} className="text-[11px] truncate flex-1" />
                    <span className="font-mono text-[9px] text-white/40 shrink-0">T{it.tier} {it.slot || it.kind}</span>
                  </label>
                );
              })}
            </div>
            <PickerPager page={itemPageSafe} pages={itemPages} onPage={setItemPage} total={filteredItems.length} testid={`${testidPrefix}-items-pager`} />
          </>
        )}
        {tab === "tokens" && (
          <div className="space-y-1 max-h-60 overflow-y-auto" data-testid={`${testidPrefix}-tokens`}>
            {tokens.length === 0 && <span className="text-white/40 text-xs">No tokens on hand.</span>}
            {tokens.map((t) => {
              const q = Number(selection.tokens?.[t.type]) || 0;
              return (
                <div key={t.type} className="flex items-center justify-between rounded border border-white/10 bg-[hsl(220_18%_10%)] px-2 py-1.5"
                  data-testid={`${testidPrefix}-token-${t.type}`}>
                  <span className="text-[11px] text-white/70 truncate flex items-center gap-1">
                    <InventoryIcon item={t} className="h-3 w-3" /><InventoryName item={t} className="text-[11px]" />
                    <span className="text-white/30">({t.qty})</span>
                  </span>
                  <Stepper value={q} max={t.qty} onChange={(nq) => setSelection({ ...selection, tokens: { ...(selection.tokens || {}), [t.type]: nq } })}
                    testid={`${testidPrefix}-token-${t.type}-step`} />
                </div>
              );
            })}
          </div>
        )}
        {tab === "materials" && (
          <div className="space-y-1 max-h-60 overflow-y-auto" data-testid={`${testidPrefix}-materials`}>
            {materials.length === 0 && <span className="text-white/40 text-xs">No materials in your satchel.</span>}
            {materials.map((m) => {
              const q = Number(selection.materials?.[m.key]) || 0;
              const MI = pickMaterialIcon(m.key);
              return (
                <div key={m.key} className="flex items-center justify-between rounded border border-white/10 bg-[hsl(220_18%_10%)] px-2 py-1.5"
                  data-testid={`${testidPrefix}-material-${m.key}`}>
                  <span className="text-[11px] text-white/70 truncate flex items-center gap-1">
                    <InventoryIcon item={{ ...m, kind: "material" }} className="h-3 w-3" /><InventoryName item={{ ...m, kind: "material" }} className="text-[11px]" />
                    <span className="text-white/30">({m.qty})</span>
                  </span>
                  <Stepper value={q} max={m.qty} onChange={(nq) => setSelection({ ...selection, materials: { ...(selection.materials || {}), [m.key]: nq } })}
                    testid={`${testidPrefix}-material-${m.key}-step`} />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Persistent selection summary — always visible at the bottom so the
       *  poster can see exactly what they're offering across every tab. */}
      {totalCount > 0 && (
        <div className="border-t border-white/10 bg-black/30 p-2" data-testid={`${testidPrefix}-summary`}>
          <div className="flex items-center gap-2 mb-1.5">
            <HandCoins className="h-3 w-3 text-emerald-300" />
            <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-emerald-300">Offering · {totalCount} item{totalCount === 1 ? "" : "s"}</span>
            <button
              type="button"
              data-testid={`${testidPrefix}-summary-clear`}
              onClick={() => setSelection({ cardIds: [], packIds: [], itemIds: [], tokens: {}, materials: {} })}
              className="ml-auto text-[9px] font-mono uppercase tracking-[0.22em] text-white/45 hover:text-red-300"
            >
              Clear all
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {selCards.map((c) => (
              <SelChip
                key={`c-${c.id}`}
                testid={`${testidPrefix}-summary-card-${c.id}`}
                label={c.title || "Card"}
                color={RARITY_COLORS[c.rarity] || "#9ca3af"}
                icon={ShieldCheck}
                onRemove={() => toggle("cardIds", c.id)}
              />
            ))}
            {selPacks.map((p) => (
              <SelChip
                key={`p-${p.id}`}
                testid={`${testidPrefix}-summary-pack-${p.id}`}
                label={p.pack_name || "Card Pack"}
                color="#f472b6"
                icon={Package}
                onRemove={() => toggle("packIds", p.id)}
              />
            ))}
            {selItems.map((it) => (
              <SelChip
                key={`i-${it.id}`}
                testid={`${testidPrefix}-summary-item-${it.id}`}
                label={`${it.name} · T${it.tier}`}
                color="#fbbf24"
                icon={pickGearIcon(it)}
                onRemove={() => toggle("itemIds", it.id)}
              />
            ))}
            {selTokenEntries.map(([type, qty]) => {
              const t = tokens.find((x) => x.type === type);
              return (
                <SelChip
                  key={`t-${type}`}
                  testid={`${testidPrefix}-summary-token-${type}`}
                  label={`${qty}× ${t?.name || type}`}
                  color={t?.color || "#38bdf8"}
                  icon={Ticket}
                  onRemove={() => setSelection({ ...selection, tokens: { ...(selection.tokens || {}), [type]: 0 } })}
                />
              );
            })}
            {selMatEntries.map(([key, qty]) => {
              const m = materials.find((x) => x.key === key);
              return (
                <SelChip
                  key={`m-${key}`}
                  testid={`${testidPrefix}-summary-mat-${key}`}
                  label={`${qty}× ${m?.name || key}`}
                  color="#a3e635"
                  icon={pickMaterialIcon(key)}
                  onRemove={() => setSelection({ ...selection, materials: { ...(selection.materials || {}), [key]: 0 } })}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/** Compact chip used inside the picker's persistent selection summary. */
function SelChip({ label, color, icon: Icon, onRemove, testid }) {
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1 rounded-full border pl-1.5 pr-1 py-0.5 text-[10px] max-w-[180px]"
      style={{ borderColor: `${color}55`, background: `${color}12`, color }}
    >
      <Icon className="h-3 w-3 shrink-0" />
      <span className="truncate text-white/85">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="ml-0.5 rounded-full p-0.5 hover:bg-white/10 text-white/50 hover:text-white"
        aria-label={`Remove ${label}`}
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </span>
  );
}

/** Prev/Next pager with a clean 1/N readout. */
function PickerPager({ page, pages, onPage, total, testid }) {
  if (pages <= 1) {
    return <div className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.22em] text-white/35" data-testid={testid}>{total} item{total === 1 ? "" : "s"}</div>;
  }
  return (
    <div className="mt-1.5 flex items-center justify-between" data-testid={testid}>
      <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/35">
        {total} item{total === 1 ? "" : "s"} · page {page}/{pages}
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPage(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="px-2 py-0.5 rounded-md border border-white/10 text-[10px] text-white/70 hover:text-white hover:bg-white/5 disabled:opacity-30"
          data-testid={`${testid}-prev`}
        >
          Prev
        </button>
        <button
          type="button"
          onClick={() => onPage(Math.min(pages, page + 1))}
          disabled={page >= pages}
          className="px-2 py-0.5 rounded-md border border-white/10 text-[10px] text-white/70 hover:text-white hover:bg-white/5 disabled:opacity-30"
          data-testid={`${testid}-next`}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${active ? "bg-white/10 text-white" : "text-white/50 hover:text-white/80 hover:bg-white/5"}`}>
      {children}
    </button>
  );
}

/* Pure-display bounty/offer summary — used inside cards and offer rows. */
function BundleSummary({ credits, cards, packs, items, tokens, materials, size = "sm" }) {
  const nothing = !credits && !cards?.length && !packs?.length && !items?.length && !tokens?.length && !materials?.length;
  if (nothing) return <span className="text-white/30 text-xs italic">Nothing yet</span>;
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {credits > 0 && (
        <span data-testid="bs-credits" className="inline-flex items-center gap-1 rounded-md bg-amber-400/10 border border-amber-400/30 px-2 py-0.5 text-[11px] text-amber-300 font-mono">
          <Coins className="h-3 w-3" /> {Number(credits).toLocaleString()}
        </span>
      )}
      {(cards || []).map((c) => <CardThumb key={c.id} card={c} size={size === "md" ? "sm" : "xs"} />)}
      {(packs || []).map((p) => (
        <span key={p.id} className="inline-flex items-center gap-1 rounded-md bg-fuchsia-400/10 border border-fuchsia-400/30 px-2 py-0.5 text-[11px] text-fuchsia-200">
          <Package className="h-3 w-3" /> {p.pack_name || "Pack"}
        </span>
      ))}
      {(items || []).map((it) => {
        const GI = pickGearIcon(it);
        return (
          <span key={it.id} title={it.desc} className="inline-flex items-center gap-1 rounded-md bg-amber-400/10 border border-amber-400/40 px-2 py-0.5 text-[11px] text-amber-200">
            <InventoryIcon item={it} className="h-3 w-3" /> T{it.tier} <InventoryName item={it} className="text-[11px]" />
          </span>
        );
      })}
      {(tokens || []).map((t) => (
        <span key={t.type} className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px]" style={{ borderColor: `${t.color}55`, color: t.color, background: `${t.color}14` }}>
          <InventoryIcon item={t} className="h-3 w-3" /> {t.qty}× <InventoryName item={t} className="text-[11px]" />
        </span>
      ))}
      {(materials || []).map((m) => {
        const MI = pickMaterialIcon(m.key);
        return (
          <span key={m.key} className="inline-flex items-center gap-1 rounded-md bg-lime-400/10 border border-lime-400/40 px-2 py-0.5 text-[11px] text-lime-200">
            <InventoryIcon item={{ ...m, kind: "material" }} className="h-3 w-3" /> {m.qty}× <InventoryName item={{ ...m, kind: "material" }} className="text-[11px]" />
          </span>
        );
      })}
    </div>
  );
}

/* Describes what a requester WANTS back (opposite of the bounty). */
function WantSummary({ req }) {
  const bits = [];
  // Direct-trade requests have a single non-negotiable "want": the target
  // card itself. Surface it first so the row never reads as an empty stub.
  // Non-privileged viewers get `is_targeted=true` but no `target_card` — we
  // still show a redacted "for a specific card" chip so the row is honest
  // about being a private trade instead of an open bounty.
  if (req.is_targeted) {
    bits.push({ k: "target-card", el: (
      <span key="target-card" className="inline-flex items-center gap-1 rounded-md bg-cyan-400/10 border border-cyan-300/40 px-2 py-0.5 text-[11px] text-cyan-200 font-medium">
        <Lock className="h-3 w-3" />
        {req.target_card?.title
          ? <>This card · <span className="text-white">{req.target_card.title}</span></>
          : "One specific card (private)"}
      </span>
    )});
  }
  if (req.tags?.length) bits.push({ k: "tags", el: (
    <span key="tags" className="flex flex-wrap gap-1">
      {req.tags.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 rounded-full bg-white/5 border border-white/10 px-2 py-0.5 text-[10px] text-white/60"><TagIcon className="h-2.5 w-2.5" />{t}</span>
      ))}
    </span>
  )});
  if (req.want_rarity) bits.push({ k: "rarity", el: (
    <span key="rarity" className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-mono uppercase" style={{ borderColor: `${RARITY_COLORS[req.want_rarity]}66`, color: RARITY_COLORS[req.want_rarity], background: `${RARITY_COLORS[req.want_rarity]}14` }}>
      {req.want_rarity}+ card
    </span>
  )});
  if (req.want_credits > 0) bits.push({ k: "creds", el: (
    <span key="creds" className="inline-flex items-center gap-1 rounded-md bg-amber-400/10 border border-amber-400/30 px-2 py-0.5 text-[11px] text-amber-300 font-mono">
      <Coins className="h-3 w-3" /> {Number(req.want_credits).toLocaleString()}
    </span>
  )});
  if (req.want_item_slots?.length || req.want_item_tier) bits.push({ k: "gear", el: (
    <span key="gear" className="inline-flex items-center gap-1 rounded-md bg-amber-400/10 border border-amber-400/40 px-2 py-0.5 text-[11px] text-amber-200">
      <Wrench className="h-3 w-3" /> {req.want_item_tier ? `T${req.want_item_tier}+` : ""} {req.want_item_slots?.join(" / ") || "gear"}
    </span>
  )});
  (req.want_tokens || []).forEach((t) => bits.push({ k: `tok-${t.type}`, el: (
    <span key={`tok-${t.type}`} className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px]" style={{ borderColor: `${t.color}55`, color: t.color, background: `${t.color}14` }}>
      <InventoryIcon item={t} className="h-3 w-3" /> {t.qty}× <InventoryName item={t} className="text-[11px]" />
    </span>
  )}));
  (req.want_materials || []).forEach((m) => bits.push({ k: `mat-${m.key}`, el: (() => {
    const MI = pickMaterialIcon(m.key);
    return (
      <span key={`mat-${m.key}`} className="inline-flex items-center gap-1 rounded-md bg-lime-400/10 border border-lime-400/40 px-2 py-0.5 text-[11px] text-lime-200">
        <InventoryIcon item={{ ...m, kind: "material" }} className="h-3 w-3" /> {m.qty}× <InventoryName item={{ ...m, kind: "material" }} className="text-[11px]" />
      </span>
    );
  })() }));
  if (bits.length === 0) return <span className="text-white/30 text-xs italic">(open — any offer welcome)</span>;
  return <div className="flex flex-wrap gap-1.5">{bits.map((b) => b.el)}</div>;
}

/* ─── Post-a-request dialog ─────────────────────────────────────────── */

function PostRequestDialog({ myCards, myInv, prefillWantTitle, prefillTargetCardId, prefillTargetOwnerId, prefillTargetOwnerName, onPosted, openState }) {
  const [openInternal, setOpenInternal] = useState(false);
  const open = openState?.open ?? openInternal;
  const setOpen = openState?.setOpen ?? setOpenInternal;

  const [title, setTitle] = useState(prefillWantTitle ? `Trade for: ${prefillWantTitle}` : "");
  const [desc, setDesc] = useState("");
  const [tags, setTags] = useState([]);
  const [wantRarity, setWantRarity] = useState("");
  const [wantCredits, setWantCredits] = useState(0);
  const [wantItemSlots, setWantItemSlots] = useState([]);
  const [wantItemTier, setWantItemTier] = useState("");
  const [wantTokens, setWantTokens] = useState({});
  const [wantMaterials, setWantMaterials] = useState({});
  const [bounty, setBounty] = useState({ cardIds: [], packIds: [], itemIds: [], tokens: {}, materials: {} });
  const isDirect = !!prefillTargetCardId;
  const { user } = useAuth();
  const [bountyCredits, setBountyCredits] = useState(isDirect ? 0 : 500);
  const [step, setStep] = useState(0);
  useEffect(() => { if (open) setStep(0); }, [open, prefillTargetCardId]);
  const target = useQuery({ queryKey: ["direct-trade-target", user?.id, prefillTargetCardId],
    queryFn: async ({ signal }) => (await api.get(`/request-board/targets/${encodeURIComponent(prefillTargetCardId)}`, { signal })).data,
    enabled: open && isDirect && !!user, retry: false, staleTime: 0 });
  const payload = {
    title: isDirect ? `Trade for ${target.data?.serial || prefillTargetCardId}`.slice(0, 120) : (title || "Trade request"),
    description: desc, tags: isDirect ? [] : tags,
    want_rarity: isDirect ? null : wantRarity || null, want_credits: Number(wantCredits) || 0,
    want_item_slots: isDirect ? [] : wantItemSlots, want_item_tier: !isDirect && wantItemTier ? Number(wantItemTier) : null,
    want_tokens: isDirect ? {} : wantTokens, want_materials: isDirect ? {} : wantMaterials,
    target_card_id: prefillTargetCardId || null, target_user_id: target.data?.owner_id || prefillTargetOwnerId || null,
    offer_credits: Number(bountyCredits) || 0, offer_card_ids: bounty.cardIds || [],
    offer_pack_ids: bounty.packIds || [], offer_item_ids: bounty.itemIds || [],
    offer_tokens: bounty.tokens || {}, offer_materials: bounty.materials || {},
  };
  const valuation = useTradeDraftQuote("/request-board/quote", { ...payload, title: "Trade quote", description: "" },
    open && (!isDirect || !!target.data));
  const cardRows = Array.isArray(myCards) ? myCards : myCards?.cards || myCards?.items || [];
  const reviewAssets = {
    cards: cardRows.filter(c => bounty.cardIds?.includes(c.id)),
    packs: (myInv?.packs || []).filter(p => bounty.packIds?.includes(p.id)),
    items: (myInv?.items || []).filter(i => bounty.itemIds?.includes(i.id)),
    tokens: (myInv?.tokens || []).filter(t => bounty.tokens?.[t.type] > 0).map(t => ({ ...t, qty: bounty.tokens[t.type] })),
    materials: (myInv?.materials || []).filter(m => bounty.materials?.[m.key] > 0).map(m => ({ ...m, qty: bounty.materials[m.key] })),
  };

  useEffect(() => { if (prefillWantTitle) setTitle(`Trade for: ${prefillWantTitle}`); }, [prefillWantTitle]);

  const create = useMutation({
    mutationFn: async () => (await api.post("/request-board", payload)).data,
    onSuccess: () => {
      toast.success(prefillTargetCardId ? "Direct offer sent — the owner will see it in their Activity." : "Request posted to the board!");
      setOpen(false);
      setTitle(""); setDesc(""); setTags([]); setWantRarity(""); setWantCredits(0);
      setWantItemSlots([]); setWantItemTier(""); setWantTokens({}); setWantMaterials({});
      setBounty({ cardIds: [], packIds: [], itemIds: [], tokens: {}, materials: {} });
      setBountyCredits(isDirect ? 0 : 500); setStep(0);
      onPosted();
    },
    onError: (e) => toast.error(apiError(e, "Couldn't post")),
  });

  // Direct-trade mode: this dialog was opened from a specific card page. The
  // target card IS the want, so we swap the WHOLE "what you want back"
  // section out for a compact locked-in banner and skip the tags / rarity /
  // credits-wanted / gear / tokens / materials pickers entirely. The
  // request already routes privately because `target_card_id` is set on the
  // backend — non-privileged users can't see it on the public board.
  const wantHas = isDirect
    ? true    // the target card IS the want; no other criteria needed
    : (tags.length || wantRarity || Number(wantCredits) > 0 || wantItemSlots.length ||
       wantItemTier || Object.values(wantTokens || {}).some((q) => q > 0) ||
       Object.values(wantMaterials || {}).some((q) => q > 0));
  const bountyHas = Number(bountyCredits) > 0 || bounty.cardIds?.length || bounty.packIds?.length ||
                    bounty.itemIds?.length || Object.values(bounty.tokens || {}).some((q) => q > 0) ||
                    Object.values(bounty.materials || {}).some((q) => q > 0);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!openState && (
        <DialogTrigger asChild>
          <Button data-testid="rb-post-button-legacy" className="bg-cyan-400 text-black hover:bg-cyan-300"><Plus className="h-4 w-4 mr-1" /> Post a Trade Request</Button>
        </DialogTrigger>
      )}
      <DialogContent data-testid="rb-post-dialog" className="z-[90] bg-[hsl(222_28%_9%)] border-white/10 text-white max-w-3xl max-h-[92dvh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="p-5 pb-3 pr-12 shrink-0">
          <DialogTitle className="font-display tracking-[0.06em]">
            {isDirect ? "DIRECT TRADE OFFER" : "POST A TRADE REQUEST"}
          </DialogTitle>
          <DialogDescription className="text-white/50">
            {isDirect
              ? "A private request for this exact card. Both sides must match in value."
              : "Choose what you want, build your offer, then review the exchange."}
          </DialogDescription>
        </DialogHeader>
        <ol data-testid="rb-wizard-steps" className="grid grid-cols-3 gap-2 border-y border-white/10 px-5 py-3 shrink-0">
          {["Want", "Offer", "Review"].map((label, index) => <li key={label} data-testid={`rb-step-${label.toLowerCase()}`} aria-current={step === index ? "step" : undefined}
            className={`text-xs font-mono ${step === index ? "text-cyan-200" : "text-white/35"}`}>{index + 1}. {label}</li>)}
        </ol>
        <div className="min-h-0 overflow-y-auto p-5 space-y-5">

        {isDirect && <div data-testid="rb-direct-banner">
          {target.isPending ? <p data-testid="rb-target-loading" role="status" className="text-sm text-white/60">Loading the selected card…</p>
            : target.isError ? <div data-testid="rb-target-error" role="alert" className="text-sm text-amber-200">{apiError(target.error, "The selected card is unavailable.")} <Button data-testid="rb-target-retry" variant="ghost" onClick={() => target.refetch()}>Retry</Button></div>
            : <TradeTargetCard card={target.data} prefix="rb-wanted-card" />}
        </div>}

        {prefillTargetCardId && !isDirect && (
          <div data-testid="rb-targeting-banner" className="rounded-xl border border-fuchsia-400/30 bg-fuchsia-400/[0.06] p-3 flex items-start gap-2">
            <ShieldCheck className="h-4 w-4 text-fuchsia-300 shrink-0 mt-0.5" />
            <div className="text-[12px] leading-snug">
              <div className="font-semibold text-fuchsia-100">Targeting <span className="text-white">{prefillTargetOwnerName || "the owner"}</span>&apos;s card{prefillWantTitle ? <>: <span className="text-white">{prefillWantTitle}</span></> : ""}</div>
              <div className="text-white/60">This request is <span className="text-fuchsia-200">private</span> — only you and the owner will see it on the board. The card itself stays hidden from every other collector until they engage with your offer.</div>
            </div>
          </div>
        )}

        <div className="space-y-5">
          {/* ── Title + description (hidden in direct-trade mode; title is auto-filled) ── */}
          {!isDirect && step === 0 && (
          <section className="space-y-3">
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Title</Label>
              <Input data-testid="rb-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" placeholder="e.g. Wanted: rare or better redhead" />
            </div>
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Details (optional)</Label>
              <Textarea data-testid="rb-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" placeholder="Vibe, story, deal-breakers…" />
            </div>
          </section>
          )}
          {isDirect && step === 0 && (
            <div>
              <Label htmlFor="rb-direct-want-credits" className="text-white/70 text-xs">Additional credits wanted</Label>
              <Input id="rb-direct-want-credits" data-testid="rb-direct-want-credits" type="number" min="0" step="1" value={wantCredits} onChange={e => setWantCredits(e.target.value)} className="mt-1 mb-4 bg-white/5 border-white/10" />
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Message (optional)</Label>
              <Textarea data-testid="rb-direct-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" placeholder="A short note to the owner — why you want it, or what you're willing to sweeten with…" />
            </div>
          )}

          {/* ── WHAT I WANT ── (hidden in direct-trade mode; the target card is the sole want) */}
          {!isDirect && step === 0 && (
          <section className="rounded-xl border border-cyan-400/25 bg-cyan-400/[0.04] p-3 space-y-3" data-testid="rb-want-section">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300/80 flex items-center gap-1.5"><ShieldCheck className="h-3 w-3" /> What you want back</div>
            <div>
              <Label className="text-white/70 text-[11px] uppercase tracking-[0.15em]">Card tags</Label>
              <div className="mt-1"><TagInput tags={tags} setTags={setTags} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/70 text-[11px] uppercase tracking-[0.15em]">Min rarity</Label>
                <Select value={wantRarity || "any"} onValueChange={(v) => setWantRarity(v === "any" ? "" : v)}>
                  <SelectTrigger className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" data-testid="rb-want-rarity"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent><SelectItem value="any">Any</SelectItem>
                    {RARITY_ORDER.map((r) => <SelectItem key={r} value={r}>{r}+</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/70 text-[11px] uppercase tracking-[0.15em] flex items-center gap-1"><Coins className="h-3 w-3 text-amber-300" /> Credits wanted</Label>
                <Input data-testid="rb-want-credits" type="number" min={0} value={wantCredits} onChange={(e) => setWantCredits(e.target.value)} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/70 text-[11px] uppercase tracking-[0.15em]">Gear slots (optional)</Label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {GEAR_SLOTS.map((s) => (
                    <button key={s} type="button" data-testid={`rb-want-slot-${s}`}
                      onClick={() => setWantItemSlots((p) => p.includes(s) ? p.filter((x) => x !== s) : [...p, s])}
                      className={`px-2 py-1 rounded border text-[10px] uppercase tracking-[0.1em] transition-colors ${wantItemSlots.includes(s) ? "border-amber-400/60 bg-amber-400/10 text-amber-200" : "border-white/10 text-white/50 hover:border-white/25"}`}>{s}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-white/70 text-[11px] uppercase tracking-[0.15em]">Min gear tier</Label>
                <Select value={wantItemTier || "any"} onValueChange={(v) => setWantItemTier(v === "any" ? "" : v)}>
                  <SelectTrigger className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" data-testid="rb-want-tier"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent><SelectItem value="any">Any</SelectItem>
                    {[1, 2, 3, 4, 5].map((t) => <SelectItem key={t} value={String(t)}>T{t}+</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Tokens/materials wanted */}
            <TokenMatWant tokens={myInv?.tokens} materials={myInv?.materials}
              wantTokens={wantTokens} setWantTokens={setWantTokens}
              wantMaterials={wantMaterials} setWantMaterials={setWantMaterials} />
          </section>
          )}

          {/* ── BOUNTY ── */}
          {step === 1 && <section className="space-y-3" data-testid="rb-bounty-section">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-300/80 flex items-center gap-1.5"><Coins className="h-3 w-3" /> Your bounty (escrowed until closed)</div>
            <div>
              <Label className="text-white/70 text-[11px] uppercase tracking-[0.15em] flex items-center gap-1"><Coins className="h-3 w-3 text-amber-300" /> Credits</Label>
              <Input data-testid="rb-credits-input" type="number" min={0} value={bountyCredits} onChange={(e) => setBountyCredits(e.target.value)} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
            </div>
            <div>
              <Label className="text-white/70 text-[11px] uppercase tracking-[0.15em]">Your assets</Label>
              <div className="mt-1">
                <InventoryStakePicker inv={myInv} cards={myCards} selection={bounty} setSelection={setBounty} testidPrefix="rb-bounty" />
              </div>
            </div>
          </section>}

          {step === 2 && <section data-testid="rb-review" className="space-y-4">
            {!isDirect && <div><p className="text-xs text-cyan-200 mb-2">You want</p><WantSummary req={{ title, tags, want_rarity: wantRarity, want_credits: Number(wantCredits) || 0,
              want_item_slots: wantItemSlots, want_item_tier: wantItemTier,
              want_tokens: (myInv?.tokens || []).filter(t => wantTokens[t.type] > 0).map(t => ({ ...t, qty: wantTokens[t.type] })),
              want_materials: (myInv?.materials || []).filter(m => wantMaterials[m.key] > 0).map(m => ({ ...m, qty: wantMaterials[m.key] })) }} /></div>}
            {isDirect && Number(wantCredits) > 0 && <p data-testid="rb-review-wanted-credits" className="text-sm text-cyan-200">Plus {Number(wantCredits).toLocaleString()} credits wanted</p>}
            <div><p className="text-xs text-amber-200 mb-2">You offer</p><BundleSummary credits={Number(bountyCredits) || 0} {...reviewAssets} /></div>
            {desc && <p data-testid="rb-review-message" className="text-xs text-white/60 break-words">{desc}</p>}
          </section>}
          {step > 0 && <TradeValueSummary {...valuation} prefix="rb-value" onBalance={step === 1 ? () => setBountyCredits(Number(bountyCredits || 0) + valuation.quote.difference) : undefined} />}
          {step === 1 && !bountyHas && (
            <p className="text-[11px] text-amber-300/70 text-center">
              {isDirect
                ? "Stake a bounty — credits or one of your assets — so the owner has something to accept."
                : (!wantHas && !bountyHas ? "Tell the board what you want AND what you'll trade for it." : !wantHas ? "Say what you want back." : "Stake a bounty.")}
            </p>
          )}
        </div>
        </div>
        <div className="flex flex-wrap justify-between gap-2 border-t border-white/10 p-4 shrink-0 bg-[hsl(222_28%_9%)]">
          <Button data-testid="rb-wizard-back" variant="outline" className="border-white/20 text-white" disabled={create.isPending}
            onClick={() => step ? setStep(s => s - 1) : setOpen(false)}>{step ? "Back" : "Cancel"}</Button>
          {step < 2 ? <Button data-testid="rb-wizard-next" className="bg-cyan-400 text-black hover:bg-cyan-300"
            disabled={step === 0 ? !wantHas || (!isDirect && title.trim().length < 2) || (isDirect && (!target.data || !!target.data.trade_lock_reason || target.isFetching)) : !bountyHas || valuation.loading || !valuation.quote?.can_post}
            onClick={() => setStep(s => s + 1)}>{step === 0 ? "Choose offer" : "Review trade"}</Button>
            : <Button data-testid="rb-submit-request" disabled={create.isPending || valuation.loading || !valuation.quote?.can_post}
              onClick={() => create.mutate()} className="bg-cyan-400 text-black hover:bg-cyan-300">{create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : isDirect ? "Send direct offer" : "Post request"}</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TokenMatWant({ tokens, materials, wantTokens, setWantTokens, wantMaterials, setWantMaterials }) {
  const [expanded, setExpanded] = useState(false);
  const activeTokens = Object.entries(wantTokens || {}).filter(([, q]) => Number(q) > 0);
  const activeMats = Object.entries(wantMaterials || {}).filter(([, q]) => Number(q) > 0);
  return (
    <div className="rounded-lg border border-white/10 bg-black/20">
      <button type="button" onClick={() => setExpanded((v) => !v)} data-testid="rb-want-tokmat-toggle"
        className="w-full flex items-center justify-between px-3 py-2 text-[11px] text-white/70 hover:bg-white/5">
        <span className="flex items-center gap-2 flex-wrap">
          <Ticket className="h-3 w-3 text-cyan-300" /> Tokens & Materials wanted
          {activeTokens.length > 0 && <span className="text-cyan-300 font-mono">· {activeTokens.length} token(s)</span>}
          {activeMats.length > 0 && <span className="text-lime-300 font-mono">· {activeMats.length} mat(s)</span>}
        </span>
        <span className="text-white/40 text-[10px]">{expanded ? "hide" : "add"}</span>
      </button>
      {expanded && (
        <div className="p-2 space-y-1 border-t border-white/5 max-h-56 overflow-y-auto">
          {(tokens || []).map((t) => {
            const q = Number(wantTokens?.[t.type]) || 0;
            return (
              <div key={t.type} className="flex items-center justify-between rounded border border-white/10 bg-[hsl(220_18%_10%)] px-2 py-1.5"
                data-testid={`rb-want-token-${t.type}`}>
                <span className="flex items-center gap-1"><InventoryIcon item={t} className="h-3 w-3" /><InventoryName item={t} className="text-[11px]" /></span>
                <Stepper value={q} onChange={(nq) => setWantTokens({ ...(wantTokens || {}), [t.type]: nq })} testid={`rb-want-token-${t.type}-step`} />
              </div>
            );
          })}
          {(materials || []).map((m) => {
            const q = Number(wantMaterials?.[m.key]) || 0;
            const MIcon = pickMaterialIcon(m.key);
            return (
              <div key={m.key} className="flex items-center justify-between rounded border border-white/10 bg-[hsl(220_18%_10%)] px-2 py-1.5"
                data-testid={`rb-want-material-${m.key}`}>
                <span className="flex items-center gap-1"><InventoryIcon item={{ ...m, kind: "material" }} className="h-3 w-3" /><InventoryName item={{ ...m, kind: "material" }} className="text-[11px]" /></span>
                <Stepper value={q} onChange={(nq) => setWantMaterials({ ...(wantMaterials || {}), [m.key]: nq })} testid={`rb-want-material-${m.key}-step`} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── Offer dialog (respond) ─────────────────────────────────────────── */

function OfferDialog({ request, myCards, myInv, onDone, counter = false }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState({ cardIds: [], packIds: [], itemIds: [], tokens: {}, materials: {} });
  const [credits, setCredits] = useState(0);
  const [note, setNote] = useState("");
  const targetId = request.target_card_id || request.target_card?.id;
  useEffect(() => {
    if (open && targetId) {
      setSel(previous => ({ ...previous, cardIds: Array.from(new Set([targetId, ...(previous.cardIds || [])])) }));
      setCredits(value => Math.max(Number(value) || 0, Number(request.want_credits) || 0));
    }
  }, [open, targetId, request.want_credits]);
  const offerPayload = { card_ids: sel.cardIds || [], credits: Number(credits) || 0,
    pack_ids: sel.packIds || [], item_ids: sel.itemIds || [], tokens: sel.tokens || {}, materials: sel.materials || {} };
  const valuation = useTradeDraftQuote(`/request-board/${request.id}/offers/quote`, offerPayload, open);
  const pendingDirect = !!targetId && request.my_offer?.status === "pending";
  const withdraw = useMutation({
    mutationFn: async () => api.post(`/request-board/offers/${request.my_offer.id}/withdraw`),
    onSuccess: () => { toast.success("Offer withdrawn. Your reserved assets have been released."); onDone(); },
    onError: error => toast.error(apiError(error, "Couldn't withdraw this offer.")),
  });

  const offer = useMutation({
    mutationFn: async () => (await api.post(`/request-board/${request.id}/offers`, { ...offerPayload, note })).data,
    onSuccess: () => { toast.success(counter ? "Counter-offer sent!" : "Offer sent!"); setOpen(false); setSel({ cardIds: [], packIds: [], itemIds: [], tokens: {}, materials: {} }); setCredits(0); setNote(""); onDone(); },
    onError: (e) => toast.error(apiError(e, "Couldn't send offer")),
  });

  const has = Number(credits) > 0 || sel.cardIds?.length || sel.packIds?.length || sel.itemIds?.length ||
              Object.values(sel.tokens || {}).some((q) => q > 0) || Object.values(sel.materials || {}).some((q) => q > 0);

  if (pendingDirect) return <Button data-testid={`rb-withdraw-direct-${request.id}`} variant="outline" className="border-white/20 text-amber-200"
    disabled={withdraw.isPending} onClick={() => withdraw.mutate()}>Withdraw pending offer</Button>;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`rb-offer-button-${request.id}`} size="sm" className={counter ? "bg-fuchsia-400 text-black hover:bg-fuchsia-300" : "bg-emerald-400 text-black hover:bg-emerald-300"}>
          <HandCoins className="h-3.5 w-3.5 mr-1" /> {counter ? "Counter-Offer" : "Send an Offer"}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={`rb-offer-dialog-${request.id}`} className="z-[90] bg-[hsl(222_28%_9%)] border-white/10 text-white max-w-2xl max-h-[92dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display tracking-[0.06em]">{counter ? "COUNTER-OFFER" : "SEND AN OFFER"}</DialogTitle>
          <DialogDescription className="text-white/50">
            {counter
              ? "Put together any bundle you like. The requester sees its value and can accept or decline."
              : "Put together your bundle — any value works. The requester sees the total and decides. A direct request must include the exact wanted card."}
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-cyan-400/25 bg-cyan-400/[0.04] p-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300/80 mb-1">{request.requester_name} wants</div>
          <WantSummary req={request} />
          {request.target_card && <div className="mt-3"><TradeTargetCard card={request.target_card} prefix="rb-response-target" /></div>}
          <div className="mt-3 border-t border-white/5 pt-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-300/80 mb-1">Their bounty</div>
            <BundleSummary credits={request.offer_credits} cards={request.offer_cards} packs={request.offer_packs}
              items={request.offer_items} tokens={request.offer_tokens} materials={request.offer_material_list} />
          </div>
        </div>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-white/70 text-[11px] uppercase tracking-[0.15em] flex items-center gap-1"><Coins className="h-3 w-3 text-amber-300" /> Credits</Label>
            <Input data-testid="rb-offer-credits" type="number" min={0} value={credits} onChange={(e) => setCredits(e.target.value)} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
          </div>
          <div>
            <Label className="text-white/70 text-[11px] uppercase tracking-[0.15em]">Your assets</Label>
            <div className="mt-1">
              <InventoryStakePicker inv={myInv} cards={myCards} selection={sel} setSelection={setSel} testidPrefix="rb-offer" />
            </div>
          </div>
          <Textarea data-testid="rb-offer-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note (optional)…" className="bg-[hsl(220_18%_12%)] border-white/10" />
          <TradeValueSummary {...valuation} prefix="rb-response-value" onBalance={() => setCredits(Number(credits || 0) + valuation.quote.difference)} />
          {targetId && !sel.cardIds?.includes(targetId) && <p data-testid="rb-response-exact-card-required" role="alert" className="text-xs text-amber-200">Select the exact requested card to continue.</p>}
          <Button data-testid="rb-submit-offer" disabled={!has || offer.isPending || valuation.loading || !valuation.quote?.can_post} onClick={() => offer.mutate()} className="w-full bg-emerald-400 text-black hover:bg-emerald-300">
            {offer.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Offer"}
          </Button>
          {!has && <p className="text-[11px] text-amber-300/70 text-center">Attach at least one asset to send this offer.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Main board ─────────────────────────────────────────────────────── */

export default function RequestBoard() {
  const qc = useQueryClient();
  const { user, refresh } = useAuth();
  const [tagFilter, setTagFilter] = useState("");
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState("all");
  const [params, setParams] = useSearchParams();
  const [postOpen, setPostOpen] = useState(false);

  // Chat / card-page hand-off pre-fill (opens Post dialog primed with a target card).
  const wantCardId = params.get("want_card");
  const wantTitle = params.get("want_title");
  const wantOwner = params.get("want_owner");
  const wantOwnerName = params.get("want_owner_name");
  const [directIntent, setDirectIntent] = useState(null);
  useEffect(() => {
    if (wantCardId || wantTitle) {
      setDirectIntent(wantCardId ? { cardId: wantCardId, title: wantTitle, ownerId: wantOwner, ownerName: wantOwnerName } : null);
      setPostOpen(true);
    }
  }, [wantCardId, wantTitle, wantOwner, wantOwnerName]);
  const clearHandoff = () => setParams(previous => {
    const next = new URLSearchParams(previous);
    ["want_card", "want_title", "want_owner", "want_owner_name"].forEach(key => next.delete(key));
    return next;
  }, { replace: true });
  const changePostOpen = value => { setPostOpen(value); if (!value) clearHandoff(); };

  const { data: board } = useQuery({
    queryKey: ["request-board", user?.id, tagFilter, search, kindFilter],
    queryFn: async () => (await api.get("/request-board", {
      params: { tag: tagFilter || undefined, q: search || undefined, kind: kindFilter === "all" ? undefined : kindFilter },
    })).data,
    refetchInterval: 8000,
  });
  const { data: mine } = useQuery({
    queryKey: ["request-board-mine", user?.id],
    queryFn: async () => (await api.get("/request-board/mine")).data,
    refetchInterval: 8000,
  });
  const { data: myCards } = useQuery({ queryKey: ["cards-mine", user?.id], queryFn: async () => (await api.get("/cards/mine?limit=500")).data });
  const { data: myInv } = useQuery({ queryKey: ["inventory", user?.id], queryFn: async () => (await api.get("/inventory")).data });

  const refetchAll = () => { qc.invalidateQueries({ predicate: (q) => ["request-board", "request-board-mine", "cards-mine", "inventory", "trade-draft-quote"].includes(q.queryKey[0]) }); void refresh(); };

  const accept = useMutation({
    mutationFn: async ({ reqId, offerId }) => (await api.post(`/request-board/${reqId}/offers/${offerId}/accept`)).data,
    onSuccess: (r) => { toast.success(`Accepted! ${r.cards_received ? `${r.cards_received} card(s) received.` : "Offer settled."}`); refetchAll(); },
    onError: (e) => toast.error(apiError(e, "Couldn't accept")),
  });
  const rejectOffer = useMutation({
    mutationFn: async ({ reqId, offerId }) => (await api.post(`/request-board/${reqId}/offers/${offerId}/reject`)).data,
    onSuccess: () => { toast.success("Offer rejected — their assets are refunded"); refetchAll(); },
    onError: (e) => toast.error(apiError(e, "Couldn't reject")),
  });
  const cancel = useMutation({
    mutationFn: async (reqId) => (await api.post(`/request-board/${reqId}/cancel`)).data,
    onSuccess: () => { toast.success("Request cancelled — bounty returned"); refetchAll(); },
    onError: (e) => toast.error(apiError(e, "Couldn't cancel")),
  });
  const withdraw = useMutation({
    mutationFn: async (offerId) => (await api.post(`/request-board/offers/${offerId}/withdraw`)).data,
    onSuccess: () => { toast.success("Offer withdrawn — assets returned"); refetchAll(); },
    onError: (e) => toast.error(apiError(e, "Couldn't withdraw")),
  });

  const items = board?.items || [];
  const openMineCount = useMemo(() => (mine?.posted || []).filter((r) => r.status === "open").length, [mine]);
  const openOffersOnMineCount = useMemo(
    () => (mine?.posted || []).reduce((n, r) => n + (r.offers?.length || 0), 0),
    [mine]
  );

  return (
    <Tabs defaultValue="browse" className="w-full" data-testid="request-board">
      <TabsList className="bg-white/5 border border-white/10">
        <TabsTrigger value="browse" data-testid="rb-tab-browse" className="data-[state=active]:bg-cyan-400 data-[state=active]:text-black">Browse Board</TabsTrigger>
        <TabsTrigger value="mine" data-testid="rb-tab-mine" className="data-[state=active]:bg-cyan-400 data-[state=active]:text-black">
          My Activity {openOffersOnMineCount > 0 && <span className="ml-1 inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-emerald-400 text-black text-[10px] font-mono">{openOffersOnMineCount}</span>}
        </TabsTrigger>
      </TabsList>

      {/* Browse */}
      <TabsContent value="browse" className="mt-5 space-y-4">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
          <div className="flex gap-2 flex-1 flex-wrap">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
              <Input data-testid="rb-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="pl-8 bg-[hsl(220_18%_12%)] border-white/10" />
            </div>
            <div className="relative max-w-[180px]">
              <TagIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
              <Input data-testid="rb-tag-filter" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} placeholder="filter by tag" className="pl-8 bg-[hsl(220_18%_12%)] border-white/10" />
            </div>
            <Select value={kindFilter} onValueChange={setKindFilter}>
              <SelectTrigger className="w-[160px] bg-[hsl(220_18%_12%)] border-white/10" data-testid="rb-kind-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any want</SelectItem>
                <SelectItem value="cards">Wants cards</SelectItem>
                <SelectItem value="tokens">Wants tokens</SelectItem>
                <SelectItem value="materials">Wants materials</SelectItem>
                <SelectItem value="gear">Wants gear</SelectItem>
                <SelectItem value="credits">Wants credits</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <PostRequestDialog key={directIntent?.cardId || "new-request"} myCards={myCards} myInv={myInv} onPosted={refetchAll}
              prefillWantTitle={directIntent?.title || wantTitle}
              prefillTargetCardId={directIntent?.cardId}
              prefillTargetOwnerId={directIntent?.ownerId}
              prefillTargetOwnerName={directIntent?.ownerName}
              openState={{ open: postOpen, setOpen: changePostOpen }} />
            <Button data-testid="rb-post-button" onClick={() => { setDirectIntent(null); clearHandoff(); setPostOpen(true); }} className="bg-cyan-400 text-black hover:bg-cyan-300">
              <Plus className="h-4 w-4 mr-1" /> Post a Trade Request
            </Button>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="hud-pill rounded-2xl p-10 text-center text-white/50" data-testid="rb-empty">
            No open requests{tagFilter || search || kindFilter !== "all" ? " match your filter" : " yet — be the first to post one"}.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            <AnimatePresence>
              {items.map((req) => (
                <motion.div key={req.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="hud-pill rounded-xl p-4" data-testid={`rb-card-${req.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-display text-lg text-white tracking-[0.04em] truncate">{req.title}</div>
                      <div className="text-[11px] text-white/40 flex flex-wrap items-center gap-1.5">
                        <span>by {req.requester_name}</span>
                        {req.offer_count > 0 && <span className="text-emerald-300"> · {req.offer_count} offer{req.offer_count > 1 ? "s" : ""}</span>}
                        {req.is_targeted && (
                          <span data-testid={`rb-card-${req.id}-targeted-badge`} className="inline-flex items-center gap-1 rounded-full border border-fuchsia-400/40 bg-fuchsia-400/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-fuchsia-200">🎯 private</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {req.target_card && (
                    <div className="mt-2 flex items-center gap-2 rounded-lg border border-fuchsia-400/25 bg-fuchsia-400/[0.06] p-2">
                      <CardThumb card={req.target_card} size="sm" />
                      <div className="min-w-0">
                        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-fuchsia-300/70">{req.is_mine ? "Targeting" : "Wants your card"}</div>
                        <div className="text-white text-sm truncate">{req.target_card.title}</div>
                      </div>
                    </div>
                  )}
                  {req.description && <p className="text-white/55 text-sm mt-1.5 line-clamp-2">{req.description}</p>}
                  <div className="mt-3 pt-3 border-t border-white/5">
                    <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-cyan-300/60 mb-1.5">Wants</div>
                    <WantSummary req={req} />
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/5">
                    <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-amber-300/60 mb-1.5">Bounty</div>
                    <BundleSummary credits={req.offer_credits} cards={req.offer_cards} packs={req.offer_packs}
                      items={req.offer_items} tokens={req.offer_tokens} materials={req.offer_material_list} />
                  </div>
                  <div className="mt-3">
                    {req.is_mine ? (
                      <span className="text-[11px] text-cyan-300/70 font-mono uppercase tracking-[0.2em]">Your request</span>
                    ) : req.my_offer ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300 font-mono"><Check className="h-3.5 w-3.5" /> You&apos;ve offered</span>
                    ) : (
                      <OfferDialog request={req} myCards={myCards} myInv={myInv} onDone={refetchAll} />
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </TabsContent>

      {/* My Activity */}
      <TabsContent value="mine" className="mt-5 space-y-8">
        <section data-testid="rb-my-posts">
          <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300/80 mb-3">
            My Requests {openMineCount > 0 && <span className="text-white/50">· {openMineCount} open</span>}
          </h3>
          {(mine?.posted || []).length === 0 ? (
            <div className="hud-pill rounded-xl p-6 text-center text-white/40 text-sm">You haven&apos;t posted any requests.</div>
          ) : (
            <div className="space-y-3">
              {mine.posted.map((req) => (
                <div key={req.id} className="hud-pill rounded-xl p-4" data-testid={`rb-mypost-${req.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-display text-lg text-white tracking-[0.04em]">{req.title}</div>
                      <div className="text-[11px] text-white/40 capitalize flex items-center gap-1">
                        {req.status === "open" ? <Hourglass className="h-3 w-3" /> : <Trophy className="h-3 w-3 text-amber-300" />} {req.status}
                      </div>
                    </div>
                    {req.status === "open" && (
                      <Button data-testid={`rb-cancel-${req.id}`} size="sm" variant="ghost" className="text-red-400" onClick={() => cancel.mutate(req.id)}>Cancel</Button>
                    )}
                  </div>
                  <div className="mt-2 grid sm:grid-cols-2 gap-2 text-xs">
                    <div className="rounded-lg border border-cyan-400/20 bg-cyan-400/[0.04] p-2">
                      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-cyan-300/70 mb-1">Wants</div>
                      <WantSummary req={req} />
                    </div>
                    <div className="rounded-lg border border-amber-400/20 bg-amber-400/[0.04] p-2">
                      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-amber-300/70 mb-1">Bounty</div>
                      <BundleSummary credits={req.offer_credits} cards={req.offer_cards} packs={req.offer_packs}
                        items={req.offer_items} tokens={req.offer_tokens} materials={req.offer_material_list} />
                    </div>
                  </div>
                  {req.status === "open" && (
                    <div className="mt-3 pt-3 border-t border-white/5">
                      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40 mb-2">Offers ({(req.offers || []).length})</div>
                      {(req.offers || []).length === 0 ? (
                        <div className="text-white/40 text-xs">No offers yet — sit tight.</div>
                      ) : (
                        <div className="space-y-2">
                          {req.offers.map((o) => (
                            <div key={o.id} className="rounded-lg border border-white/10 bg-white/[0.03] p-2.5" data-testid={`rb-offer-${o.id}`}>
                              <div className="flex items-center justify-between gap-2 mb-1.5">
                                <div className="text-white text-sm">from <span className="text-cyan-200">{o.responder_name}</span></div>
                                <div className="flex gap-1.5">
                                  <Button data-testid={`rb-accept-${o.id}`} size="sm" className="bg-cyan-400 text-black hover:bg-cyan-300 h-7" disabled={accept.isPending} onClick={() => accept.mutate({ reqId: req.id, offerId: o.id })}>Accept</Button>
                                  <Button data-testid={`rb-reject-${o.id}`} size="sm" variant="ghost" className="text-red-400 h-7" disabled={rejectOffer.isPending} onClick={() => rejectOffer.mutate({ reqId: req.id, offerId: o.id })}>Reject</Button>
                                </div>
                              </div>
                              {typeof o.value === "number" && (
                                <div data-testid={`rb-offer-value-${o.id}`} className="flex items-center justify-between text-[11px] font-mono mb-1.5 rounded border border-white/10 bg-white/[0.02] px-2 py-1">
                                  <span className="text-white/45 uppercase tracking-[0.15em]">Offer value</span>
                                  {(() => {
                                    const want = typeof req.offer_value === "number" ? req.offer_value : null;
                                    const diff = want == null ? null : o.value - want;
                                    const col = diff == null ? "text-white/70" : diff > 0 ? "text-emerald-300" : diff < 0 ? "text-amber-300" : "text-cyan-300";
                                    const label = diff == null ? "" : diff > 0 ? ` · +${diff.toLocaleString()} over your ask` : diff < 0 ? ` · ${Math.abs(diff).toLocaleString()} under your ask` : " · matches your ask";
                                    return <span className={col}>{o.value.toLocaleString()}c{label}</span>;
                                  })()}
                                </div>
                              )}
                              <BundleSummary credits={o.credits} cards={o.cards} packs={o.packs} items={o.items} tokens={o.token_list} materials={o.material_list} />
                              {o.note && <div className="text-[11px] text-white/40 italic mt-1.5">“{o.note}”</div>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <section data-testid="rb-targeted-at-me">
          <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-fuchsia-300/80 mb-3">
            Direct Offers For My Cards {(mine?.targeted_at_me || []).length > 0 && <span className="text-white/50">· {mine.targeted_at_me.length}</span>}
          </h3>
          {(mine?.targeted_at_me || []).length === 0 ? (
            <div className="hud-pill rounded-xl p-6 text-center text-white/40 text-sm">No one has proposed a direct trade for one of your cards yet.</div>
          ) : (
            <div className="space-y-3">
              {mine.targeted_at_me.map((req) => (
                <div key={req.id} className="hud-pill rounded-xl p-4 border-fuchsia-400/30" data-testid={`rb-targeted-${req.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-display text-lg text-white tracking-[0.04em]">{req.title}</div>
                      <div className="text-[11px] text-white/45 flex items-center gap-1.5 flex-wrap">
                        <span>from <span className="text-cyan-200">{req.requester_name}</span></span>
                        <span>·</span>
                        <span className="capitalize">{req.status === "open" ? <span className="text-amber-300"><Hourglass className="h-3 w-3 inline mr-0.5" />awaiting your reply</span> : req.status}</span>
                      </div>
                    </div>
                  </div>
                  {req.target_card && (
                    <div className="mt-2 flex items-center gap-2 rounded-lg border border-fuchsia-400/25 bg-fuchsia-400/[0.06] p-2">
                      <CardThumb card={req.target_card} size="sm" />
                      <div className="min-w-0">
                        <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-fuchsia-200 mb-0.5">They want your card</div>
                        <div className="text-white text-sm truncate">{req.target_card.title}</div>
                      </div>
                    </div>
                  )}
                  <div className="mt-2 grid sm:grid-cols-2 gap-2 text-xs">
                    <div className="rounded-lg border border-cyan-400/20 bg-cyan-400/[0.04] p-2">
                      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-cyan-300/70 mb-1">Wants</div>
                      <WantSummary req={req} />
                    </div>
                    <div className="rounded-lg border border-amber-400/20 bg-amber-400/[0.04] p-2">
                      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-amber-300/70 mb-1">Their bounty (escrowed)</div>
                      <BundleSummary credits={req.offer_credits} cards={req.offer_cards} packs={req.offer_packs}
                        items={req.offer_items} tokens={req.offer_tokens} materials={req.offer_material_list} />
                    </div>
                  </div>
                  {req.description && (
                    <div className="mt-2 text-[12px] text-white/55 italic">&ldquo;{req.description}&rdquo;</div>
                  )}
                  {req.status === "open" && (
                    <div className="mt-3 pt-3 border-t border-white/5 flex flex-wrap gap-2 items-center">
                      {req.my_offer ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300 font-mono"><Check className="h-3.5 w-3.5" /> Counter-offer sent — waiting for their reply</span>
                      ) : (
                        <>
                          <OfferDialog request={req} myCards={myCards} myInv={myInv} onDone={refetchAll} counter />
                          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">Or counter with your own terms — negotiate until you both agree.</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <section data-testid="rb-my-offers">
          <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-emerald-300/80 mb-3">Offers I&apos;ve Made</h3>
          {(mine?.my_offers || []).length === 0 ? (
            <div className="hud-pill rounded-xl p-6 text-center text-white/40 text-sm">You haven&apos;t made any offers.</div>
          ) : (
            <div className="space-y-2">
              {mine.my_offers.map((o) => (
                <div key={o.id} className="hud-pill rounded-xl p-3" data-testid={`rb-myoffer-${o.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm truncate">For: {o.request_title}</div>
                      <div className="text-[11px] text-white/45">Bounty: <BundleSummary credits={o.bounty_credits} cards={o.bounty_cards} size="xs" /></div>
                    </div>
                    <span className={`font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded border ${o.status === "accepted" ? "text-emerald-300 border-emerald-300/30" : o.status === "pending" ? "text-amber-300 border-amber-300/30" : "text-white/40 border-white/10"}`}>{o.status}</span>
                    {o.status === "pending" && <Button data-testid={`rb-withdraw-${o.id}`} size="sm" variant="ghost" className="text-red-400" onClick={() => withdraw.mutate(o.id)}>Withdraw</Button>}
                  </div>
                  <div className="mt-2">
                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 mb-1">You offered</div>
                    <BundleSummary credits={o.credits} cards={o.cards} packs={o.packs} items={o.items} tokens={o.token_list} materials={o.material_list} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </TabsContent>
    </Tabs>
  );
}
